import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { safeMethodsApi } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
    BookOpen,
    RefreshCw,
    ShieldAlert,
    Sparkles,
    Utensils,
    Thermometer,
    Users,
    Truck,
    ChevronRight,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { capitalize } from '../lib/utils';

const categoryIcons = {
    cross_contamination: ShieldAlert,
    cleaning: Sparkles,
    cooking: Utensils,
    chilling: Thermometer,
    management: Users,
    personal_hygiene: Users,
    suppliers: Truck
};

const categoryColors = {
    cross_contamination: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    cleaning: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    cooking: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    chilling: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    management: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    personal_hygiene: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    suppliers: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
};

const SafeMethods = () => {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const activeCategory = searchParams.get('category') || 'all';

    useEffect(() => {
        loadMethods();
    }, [activeCategory]);

    const loadMethods = async () => {
        try {
            const category = activeCategory === 'all' ? null : activeCategory;
            const response = await safeMethodsApi.getAll(category);
            setMethods(response.data);
        } catch (error) {
            console.error('Failed to load safe methods:', error);
            toast.error('Failed to load safe methods');
        } finally {
            setLoading(false);
        }
    };

    const categories = [
        { id: 'all', label: 'All Methods' },
        { id: 'cross_contamination', label: 'Cross-Contamination' },
        { id: 'cleaning', label: 'Cleaning' },
        { id: 'chilling', label: 'Chilling' },
        { id: 'cooking', label: 'Cooking' },
        { id: 'personal_hygiene', label: 'Personal Hygiene' },
        { id: 'management', label: 'Management' },
        { id: 'suppliers', label: 'Suppliers' }
    ];

    const handleCategoryChange = (category) => {
        if (category === 'all') {
            searchParams.delete('category');
        } else {
            searchParams.set('category', category);
        }
        setSearchParams(searchParams);
        setSelectedMethod(null);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="safe-methods-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Safe Methods</h1>
                        <p className="text-slate-400 mt-1">
                            SFBB food safety procedures and guidelines
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadMethods}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid="refresh-methods"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Category Tabs */}
                <Tabs value={activeCategory} onValueChange={handleCategoryChange}>
                    <TabsList className="bg-slate-900 border border-slate-800 p-1 flex-wrap h-auto">
                        {categories.map((cat) => (
                            <TabsTrigger
                                key={cat.id}
                                value={cat.id}
                                className="brand-btn data-[state=active]:text-white"
                                data-testid={`category-tab-${cat.id}`}
                            >
                                {cat.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                {/* Methods Grid / Detail View */}
                {selectedMethod ? (
                    <div className="space-y-6">
                        <Button
                            variant="ghost"
                            onClick={() => setSelectedMethod(null)}
                            className="text-slate-400 hover:text-slate-200"
                            data-testid="back-to-methods"
                        >
                            ← Back to all methods
                        </Button>

                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <div className="flex items-start gap-4">
                                    <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${categoryColors[selectedMethod.category]}`}>
                                        {React.createElement(categoryIcons[selectedMethod.category] || BookOpen, { className: 'h-7 w-7' })}
                                    </div>
                                    <div className="flex-1">
                                        <CardTitle className="text-xl text-slate-100">{selectedMethod.title}</CardTitle>
                                        <Badge className={`mt-2 ${categoryColors[selectedMethod.category]}`}>
                                            {capitalize(selectedMethod.category)}
                                        </Badge>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">Description</h3>
                                    <p className="text-slate-300 leading-relaxed">{selectedMethod.description}</p>
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">Steps to Follow</h3>
                                    <ol className="space-y-2">
                                        {selectedMethod.steps.map((step, idx) => (
                                            <li key={idx} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                                                <span className="flex-shrink-0 h-6 w-6 rounded-full brand-bg-subtle brand-text text-sm font-semibold flex items-center justify-center">
                                                    {idx + 1}
                                                </span>
                                                <span className="text-slate-300">{step}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                                            <CheckCircle2 className="h-4 w-4" />
                                            Safety Points
                                        </h3>
                                        <ul className="space-y-2">
                                            {selectedMethod.safety_points.map((point, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-slate-300">
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                                                    {point}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-semibold text-rose-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4" />
                                            What To Do If Things Go Wrong
                                        </h3>
                                        <ul className="space-y-2">
                                            {selectedMethod.what_to_do_if_wrong.map((action, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-slate-300">
                                                    <AlertTriangle className="h-4 w-4 text-rose-400 mt-1 flex-shrink-0" />
                                                    {action}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                ) : (
                    <>
                        {methods.length > 0 ? (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {methods.map((method) => {
                                    const IconComponent = categoryIcons[method.category] || BookOpen;
                                    return (
                                        <Card
                                            key={method.id}
                                            className="bg-slate-900 border-slate-800 card-hover cursor-pointer"
                                            onClick={() => setSelectedMethod(method)}
                                            data-testid={`method-card-${method.id}`}
                                        >
                                            <CardContent className="pt-6">
                                                <div className="flex items-start gap-4">
                                                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${categoryColors[method.category]}`}>
                                                        <IconComponent className="h-6 w-6" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold text-slate-100 mb-1">{method.title}</h3>
                                                        <Badge className={`text-xs ${categoryColors[method.category]}`}>
                                                            {capitalize(method.category)}
                                                        </Badge>
                                                    </div>
                                                    <ChevronRight className="h-5 w-5 text-slate-600" />
                                                </div>
                                                <p className="mt-4 text-sm text-slate-400 line-clamp-2">
                                                    {method.description}
                                                </p>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        ) : (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="py-12">
                                    <div className="empty-state">
                                        <BookOpen className="empty-state-icon" />
                                        <p className="empty-state-title">No safe methods found</p>
                                        <p className="empty-state-description">
                                            {isManager ? 'Click "Initialize Safe Methods" on the dashboard to seed default SFBB procedures.' : 'Please ask your manager to initialize the safe methods.'}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default SafeMethods;
